import { useCallback, useEffect, useMemo, useState } from 'react';

interface WorkflowRow {
  workflowId: string;
  name: string;
  isPlatform: boolean;
}

interface VersionRow {
  workflowVersionId: string;
  workflowId: string;
  isActive: boolean;
}

interface HeldSelection<W extends WorkflowRow, V extends VersionRow> {
  workflowId?: string;
  workflowVersionId?: string;
  /** The created row itself, shown until the list has it. */
  workflow?: W;
  workflowVersion?: V;
}

interface UseWorkflowRouteRedirectsParams<W extends WorkflowRow, V extends VersionRow> {
  selectedWorkflowId?: string;
  selectedVersionId?: string;
  workflows: W[];
  workflowsLoading: boolean;
  workflowVersions: V[];
  versionsLoading: boolean;
  navigateToWorkflow: (
    workflowId?: string,
    versionId?: string,
    options?: { replace?: boolean },
  ) => void;
  refetchWorkflows: () => Promise<unknown>;
  refetchWorkflowVersions: () => Promise<unknown>;
}

/**
 * List reads go to the reader replica, which can lag a write by a second or more, so the first
 * reads after a create usually come back without the new row. Spaced out to cover that lag.
 */
export const HELD_ROW_REFETCH_DELAYS_MS = [500, 1000, 2000, 4000];

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Refetches on the delays above while `stale`; stops as soon as it is not. */
function useRefetchWhileStale(stale: boolean, refetch: () => Promise<unknown>) {
  useEffect(() => {
    if (!stale) {
      return undefined;
    }

    let cancelled = false;

    void (async () => {
      for (const delay of HELD_ROW_REFETCH_DELAYS_MS) {
        await wait(delay);
        if (cancelled) return;
        await refetch().catch(() => undefined);
        if (cancelled) return;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [stale, refetch]);
}

/** Own workflows by name first, then platform ones: where the list itself would put it. */
function withWorkflow<W extends WorkflowRow>(workflows: W[], workflow: W): W[] {
  const at = workflows.findIndex(
    (candidate) => candidate.isPlatform || candidate.name.localeCompare(workflow.name) > 0,
  );
  return at === -1
    ? [...workflows, workflow]
    : [...workflows.slice(0, at), workflow, ...workflows.slice(at)];
}

/**
 * Sends the page back to a clean URL when the workflow or version in it is not in the lists —
 * except for one the page has just created, which it holds until the user navigates elsewhere.
 * A URL that names a workflow without a version opens one of its versions.
 *
 * The lists cannot be trusted to have a new row straight away: they read the reader replica,
 * and they are shared cache entries that other `cache-and-network` reads write to, so a response
 * issued before the create can land after it. A held row the create handed back is merged into
 * the lists meanwhile, so the page shows it at once, and the lists are refetched on a backoff
 * until they have it.
 */
export function useWorkflowRouteRedirects<W extends WorkflowRow, V extends VersionRow>({
  selectedWorkflowId,
  selectedVersionId,
  workflows,
  workflowsLoading,
  workflowVersions,
  versionsLoading,
  navigateToWorkflow,
  refetchWorkflows,
  refetchWorkflowVersions,
}: UseWorkflowRouteRedirectsParams<W, V>) {
  const [held, setHeld] = useState<HeldSelection<W, V> | null>(null);

  const holdSelection = useCallback((selection: HeldSelection<W, V>) => {
    setHeld({
      ...selection,
      workflowId: selection.workflowId ?? selection.workflow?.workflowId,
      workflowVersionId:
        selection.workflowVersionId ?? selection.workflowVersion?.workflowVersionId,
    });
  }, []);

  const releaseSelection = useCallback(() => setHeld(null), []);

  // The same lag the other way: a version just deleted comes back from the replica as live for a
  // while, so the page drops it itself and refetches until the list agrees.
  const [deletedVersionIds, setDeletedVersionIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const hideDeletedVersion = useCallback(
    (workflowVersionId: string) =>
      setDeletedVersionIds((previous) => new Set(previous).add(workflowVersionId)),
    [],
  );
  const deletedVersionStillListed = workflowVersions.some(({ workflowVersionId }) =>
    deletedVersionIds.has(workflowVersionId),
  );
  useRefetchWhileStale(deletedVersionStillListed, refetchWorkflowVersions);

  const heldWorkflowListed =
    !!held?.workflowId &&
    workflows.some((workflow) => workflow.workflowId === held.workflowId);
  const heldVersionListed =
    !!held?.workflowVersionId &&
    workflowVersions.some(
      (workflowVersion) => workflowVersion.workflowVersionId === held.workflowVersionId,
    );

  // Not conditioned on the lists' loading flags: each refetch sets them for a moment, which would
  // restart the backoff at its first delay on every round and refetch forever.
  useRefetchWhileStale(
    !!held?.workflowId && held.workflowId === selectedWorkflowId && !heldWorkflowListed,
    refetchWorkflows,
  );
  useRefetchWhileStale(
    !!held?.workflowVersionId &&
      held.workflowVersionId === selectedVersionId &&
      !heldVersionListed,
    refetchWorkflowVersions,
  );

  const mergedWorkflows = useMemo(
    () =>
      held?.workflow && !heldWorkflowListed
        ? withWorkflow(workflows, held.workflow)
        : workflows,
    [held?.workflow, heldWorkflowListed, workflows],
  );

  const mergedWorkflowVersions = useMemo(() => {
    const listed = deletedVersionStillListed
      ? workflowVersions.filter(
          ({ workflowVersionId }) => !deletedVersionIds.has(workflowVersionId),
        )
      : workflowVersions;

    // Newest first, as the list is sorted; only for the workflow it belongs to.
    return held?.workflowVersion &&
      !heldVersionListed &&
      held.workflowVersion.workflowId === selectedWorkflowId &&
      !deletedVersionIds.has(held.workflowVersion.workflowVersionId)
      ? [held.workflowVersion, ...listed]
      : listed;
  }, [
    deletedVersionIds,
    deletedVersionStillListed,
    held?.workflowVersion,
    heldVersionListed,
    selectedWorkflowId,
    workflowVersions,
  ]);

  useEffect(() => {
    if (!selectedWorkflowId || workflowsLoading) {
      return;
    }

    if (
      held?.workflowId === selectedWorkflowId ||
      mergedWorkflows.some((workflow) => workflow.workflowId === selectedWorkflowId)
    ) {
      return;
    }

    navigateToWorkflow(undefined, undefined, { replace: true });
  }, [
    held?.workflowId,
    mergedWorkflows,
    navigateToWorkflow,
    selectedWorkflowId,
    workflowsLoading,
  ]);

  useEffect(() => {
    if (!selectedWorkflowId || !selectedVersionId || versionsLoading) {
      return;
    }

    if (
      held?.workflowVersionId === selectedVersionId ||
      mergedWorkflowVersions.some(
        (workflowVersion) => workflowVersion.workflowVersionId === selectedVersionId,
      )
    ) {
      return;
    }

    navigateToWorkflow(selectedWorkflowId, undefined, { replace: true });
  }, [
    held?.workflowVersionId,
    mergedWorkflowVersions,
    navigateToWorkflow,
    selectedVersionId,
    selectedWorkflowId,
    versionsLoading,
  ]);

  // A workflow picked without a version opens its active version, or its newest when none is
  // active. The list comes newest first.
  const defaultVersionId = useMemo(() => {
    const versions = mergedWorkflowVersions.filter(
      (workflowVersion) => workflowVersion.workflowId === selectedWorkflowId,
    );
    return (versions.find((workflowVersion) => workflowVersion.isActive) ?? versions[0])
      ?.workflowVersionId;
  }, [mergedWorkflowVersions, selectedWorkflowId]);

  useEffect(() => {
    if (!selectedWorkflowId || selectedVersionId || versionsLoading || !defaultVersionId) {
      return;
    }

    navigateToWorkflow(selectedWorkflowId, defaultVersionId, { replace: true });
  }, [
    defaultVersionId,
    navigateToWorkflow,
    selectedVersionId,
    selectedWorkflowId,
    versionsLoading,
  ]);

  return {
    workflows: mergedWorkflows,
    workflowVersions: mergedWorkflowVersions,
    holdSelection,
    releaseSelection,
    /** Drops a version the page just deleted from the list, until the server stops returning it. */
    hideDeletedVersion,
  };
}
